import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import {
  STOFFE_PRO_SEITE,
  STOFF_AUFSCHLAG_STUFEN,
  VK_SPANNE,
  formatCm,
  formatRapport,
  gewinnCents,
  leseStoffListeParams,
  mergeOptionen,
  splitListe,
  stoffListeQuery,
  vkBruttoCents,
  WERT_OHNE_KOMMA,
  type BreitenKlasse,
  type StoffEigenschaft,
  type StoffIntern,
  type StoffListeParams,
  type StoffSeite,
  type StoffSortierung,
  type StoffWerteArt,
} from "@repo/shared";

import { Bestaetigen } from "../components/stoffe/Bestaetigen";
import { useFarbabgleichLauf } from "../components/stoffe/FarbabgleichLauf";
import { MehrfachAuswahl } from "../components/stoffe/MehrfachAuswahl";
import { PreisSpanne } from "../components/stoffe/PreisSpanne";
import {
  Badges,
  EIGENSCHAFTEN,
  EigenschaftenSymbole,
  EigenschaftenZelle,
  FotoZelle,
  Leer,
  NotizZelle,
  WerteZelle,
  Zeichen,
  datumText,
  euroText,
  faktorText,
} from "../components/stoffe/StoffZellen";
import { api, errorMessage } from "../lib/api";

type StoffAbfrage = Parameters<typeof api.api.admin.stoffe.$get>[0]["query"];
type Aenderung = { typ?: string | null; material?: string | null; schwerEntflammbar?: boolean; recyclebar?: boolean };
type Frage = { art: "archivieren" | "wiederherstellen" | "loeschen"; stoff: StoffIntern };
type Auswahl = "typ" | "material" | "eigenschaften";

const VERZOEGERUNG = 300;
const MELDUNG_MS = 3000;
const PREIS_SORTIERUNG: StoffSortierung[] = ["ek", "faktor", "gewinn"];

const FRAGEN: Record<Frage["art"], { titel: string; text: (name: string) => string; knopf: string; erfolg: string }> = {
  archivieren: {
    titel: "Stoff archivieren?",
    text: (name) => `„${name}“ wandert ins Stoffarchiv und kann jederzeit wiederhergestellt werden.`,
    knopf: "Archivieren",
    erfolg: "Archiviert.",
  },
  wiederherstellen: {
    titel: "Stoff wiederherstellen?",
    text: (name) => `„${name}“ wird zurück in die Stoffliste verschoben.`,
    knopf: "Wiederherstellen",
    erfolg: "Wiederhergestellt.",
  },
  loeschen: {
    titel: "Endgültig löschen?",
    text: (name) => `„${name}“ wird unwiderruflich gelöscht. Das kann nicht rückgängig gemacht werden.`,
    knopf: "Endgültig löschen",
    erfolg: "Endgültig gelöscht.",
  },
};

function hatFilter(p: StoffListeParams): boolean {
  return Boolean(
    p.suche ||
      p.herstellerName ||
      p.breite ||
      p.typ.length ||
      p.material.length ||
      p.eigenschaften.length ||
      p.vkMin != null ||
      p.vkMax != null,
  );
}

/** Parameter der Admin-API aus dem Zustand der Liste; Seite → offset. */
function apiAbfrage(p: StoffListeParams): StoffAbfrage {
  return {
    ...(p.ansicht !== "aktiv" ? { ansicht: p.ansicht } : {}),
    ...(p.suche ? { suche: p.suche } : {}),
    ...(p.herstellerName ? { herstellerName: p.herstellerName } : {}),
    ...(p.breite ? { breite: p.breite } : {}),
    ...(p.typ.length ? { typ: p.typ.join(",") } : {}),
    ...(p.material.length ? { material: p.material.join(",") } : {}),
    ...(p.eigenschaften.length ? { eigenschaften: p.eigenschaften.join(",") } : {}),
    ...(p.vkMin != null ? { vkMin: String(p.vkMin) } : {}),
    ...(p.vkMax != null ? { vkMax: String(p.vkMax) } : {}),
    sort: p.sort,
    richtung: p.richtung,
    limit: String(STOFFE_PRO_SEITE),
    offset: String((p.seite - 1) * STOFFE_PRO_SEITE),
  };
}

/** Ein Filter mit Aktivierungs-Häkchen: wirkt nur, wenn angehakt; sonst bleibt die Eingabe gedimmt stehen. */
function FilterFeld({
  label,
  aktiv,
  onAktiv,
  breit,
  children,
}: {
  label: string;
  aktiv: boolean;
  onAktiv(aktiv: boolean): void;
  breit?: boolean;
  children: ReactNode;
}) {
  const klassen = ["stoffe-filter__feld", breit ? "stoffe-filter__feld--breit" : "", aktiv ? "" : "stoffe-filter__feld--aus"];
  return (
    <div className={klassen.filter(Boolean).join(" ")}>
      <label className="stoffe-filter__kopf">
        <input type="checkbox" checked={aktiv} onChange={(e) => onAktiv(e.target.checked)} />
        {label}
      </label>
      <div className="stoffe-filter__eingabe">{children}</div>
    </div>
  );
}

/** Ruft `fn` erst auf, wenn 300 ms keine neue Eingabe kam. */
function useVerzoegert() {
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return useMemo(
    () => ({
      planen(fn: () => void) {
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(fn, VERZOEGERUNG);
      },
      abbrechen() {
        window.clearTimeout(timer.current);
      },
    }),
    [],
  );
}

/** Stoffliste nach dem Vorbild des früheren ERP: Reiter, Filter, Preise, Bearbeiten in der Tabelle, Archiv. */
export function StoffePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const params = useMemo(() => leseStoffListeParams(Object.fromEntries(searchParams)), [searchParams]);
  const imArchiv = params.ansicht === "archiv";

  // Verzögerte Eingaben schreiben in den neuesten Zustand, nicht in den beim Tippen.
  const paramsRef = useRef(params);
  paramsRef.current = params;

  /** Jede Änderung landet in der URL; Filteränderungen springen auf Seite 1. */
  const gehe = useCallback(
    (patch: Partial<StoffListeParams>, ersetzen = false) => {
      const neu = { ...paramsRef.current, ...patch, seite: "seite" in patch ? (patch.seite ?? 1) : 1 };
      paramsRef.current = neu;
      navigate({ pathname: "/stoffe", search: stoffListeQuery(neu) }, { replace: ersetzen });
    },
    [navigate],
  );

  /* --- Laden ----------------------------------------------------------------------------- */

  const [daten, setDaten] = useState<StoffSeite | null>(null);
  // Zu welcher Abfrage `daten` gehören – die Seitenbegrenzung darf keine Antwort einer früheren Abfrage nutzen.
  const [datenAbfrage, setDatenAbfrage] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  const neuLaden = useCallback(() => setVersion((v) => v + 1), []);
  // JAB-, Sarlas- und ED-Farbabgleich für viele Stoffe; danach die Liste neu laden.
  const farbabgleich = useFarbabgleichLauf(neuLaden);
  const abfrage = useMemo(() => JSON.stringify(apiAbfrage(params)), [params]);

  useEffect(() => {
    let aktuell = true;
    setLaedt(true);
    api.api.admin.stoffe
      .$get({ query: JSON.parse(abfrage) as StoffAbfrage })
      .then(async (res) => {
        if (!res.ok) throw new Error(await errorMessage(res, "Stoffe nicht ladbar"));
        const seite = await res.json();
        if (!aktuell) return;
        if ("stoffe" in seite) {
          setDaten(seite);
          setDatenAbfrage(abfrage);
        }
        setLadeFehler(null);
      })
      .catch((cause: unknown) => {
        if (aktuell) setLadeFehler(cause instanceof Error ? cause.message : "Laden fehlgeschlagen");
      })
      .finally(() => {
        if (aktuell) setLaedt(false);
      });
    return () => {
      aktuell = false;
    };
  }, [abfrage, version]);

  const [werte, setWerte] = useState<Record<StoffWerteArt, string[]>>({ typ: [], material: [] });

  const ladeWerte = useCallback(async (art: StoffWerteArt): Promise<string[]> => {
    const res = await api.api.admin["stoff-werte"][":art"].$get({ param: { art } });
    if (!res.ok) throw new Error(await errorMessage(res, "Werte nicht ladbar"));
    const d = await res.json();
    const namen = "werte" in d ? d.werte.map((w) => w.name) : [];
    setWerte((alt) => ({ ...alt, [art]: namen }));
    return namen;
  }, []);

  /* --- Meldungen ------------------------------------------------------------------------- */

  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const meldungTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(meldungTimer.current), []);

  const melde = useCallback((text: string) => {
    setFehler(null);
    setMeldung(text);
    window.clearTimeout(meldungTimer.current);
    meldungTimer.current = window.setTimeout(() => setMeldung(null), MELDUNG_MS);
  }, []);

  const zeigeFehler = useCallback((text: string) => {
    window.clearTimeout(meldungTimer.current);
    setMeldung(null);
    setFehler(text);
  }, []);

  const [stoffAufschlag, setStoffAufschlag] = useState<number | null>(null);

  useEffect(() => {
    api.api.admin.einstellungen
      .$get()
      .then(async (res) => {
        if (!res.ok) throw new Error(await errorMessage(res, "Einstellungen nicht ladbar"));
        setStoffAufschlag((await res.json()).stoffAufschlagProzent);
      })
      .catch((cause: unknown) => zeigeFehler(cause instanceof Error ? cause.message : "Einstellungen nicht ladbar"));
  }, [zeigeFehler]);

  async function stoffAufschlagSpeichern(neu: number) {
    const alt = stoffAufschlag;
    setStoffAufschlag(neu);
    try {
      const res = await api.api.admin.einstellungen.$patch({ json: { stoffAufschlagProzent: neu } });
      if (!res.ok) throw new Error(await errorMessage(res, "Speichern fehlgeschlagen"));
      melde("Gespeichert.");
    } catch (cause) {
      setStoffAufschlag(alt);
      zeigeFehler(cause instanceof Error ? cause.message : "Speichern fehlgeschlagen");
    }
  }

  useEffect(() => {
    Promise.all([ladeWerte("typ"), ladeWerte("material")]).catch((cause: unknown) =>
      zeigeFehler(cause instanceof Error ? cause.message : "Werte nicht ladbar"),
    );
  }, [ladeWerte, zeigeFehler]);

  /* --- Filter-Entwürfe: bleiben sichtbar, wenn das Häkchen fehlt -------------------------- */

  const sucheVerzoegert = useVerzoegert();
  const herstellerVerzoegert = useVerzoegert();
  const herstellerFeld = useRef<HTMLInputElement>(null);
  const [sucheText, setSucheText] = useState(params.suche);
  const [herstellerText, setHerstellerText] = useState(params.herstellerName);
  const [breiteEntwurf, setBreiteEntwurf] = useState<BreitenKlasse>(params.breite ?? "bahnbreit");
  const [typEntwurf, setTypEntwurf] = useState(params.typ);
  const [materialEntwurf, setMaterialEntwurf] = useState(params.material);
  const [eigenschaftenEntwurf, setEigenschaftenEntwurf] = useState(params.eigenschaften);
  const [vkEntwurf, setVkEntwurf] = useState<[number, number]>([
    params.vkMin ?? VK_SPANNE.min,
    params.vkMax ?? VK_SPANNE.max,
  ]);
  const [vkZiehen, setVkZiehen] = useState<[number, number] | null>(null);
  const [auswahlOffen, setAuswahlOffen] = useState<Auswahl | null>(null);

  const entwuerfeLeeren = useCallback(() => {
    sucheVerzoegert.abbrechen();
    herstellerVerzoegert.abbrechen();
    setSucheText("");
    setHerstellerText("");
    setBreiteEntwurf("bahnbreit");
    setTypEntwurf([]);
    setMaterialEntwurf([]);
    setEigenschaftenEntwurf([]);
    setVkEntwurf([VK_SPANNE.min, VK_SPANNE.max]);
  }, [sucheVerzoegert, herstellerVerzoegert]);

  // Von außen geänderte URL (Zurück-Knopf, Reiter) ins Suchfeld übernehmen, ohne Tippen zu stören.
  useEffect(() => {
    setSucheText((t) => (t.trim() === params.suche ? t : params.suche));
  }, [params.suche]);
  useEffect(() => {
    if (params.herstellerName) setHerstellerText((t) => (t.trim() === params.herstellerName ? t : params.herstellerName));
  }, [params.herstellerName]);

  // Reiterwechsel beginnt wie im ERP ohne Filter und ohne Auswahl.
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const letzteAnsicht = useRef(params.ansicht);
  useEffect(() => {
    if (letzteAnsicht.current === params.ansicht) return;
    letzteAnsicht.current = params.ansicht;
    entwuerfeLeeren();
    setGewaehlt(null);
  }, [params.ansicht, entwuerfeLeeren]);

  const vkAktiv = params.vkMin != null || params.vkMax != null;
  const [vkVon, vkBis] =
    vkZiehen ?? (vkAktiv ? [params.vkMin ?? VK_SPANNE.min, params.vkMax ?? VK_SPANNE.max] : vkEntwurf);
  const typWerte = params.typ.length ? params.typ : typEntwurf;
  const materialWerte = params.material.length ? params.material : materialEntwurf;
  const eigenschaftenWerte = params.eigenschaften.length ? params.eigenschaften : eigenschaftenEntwurf;
  const zuruecksetzbar =
    hatFilter(params) ||
    Boolean(sucheText || herstellerText || typEntwurf.length || materialEntwurf.length || eigenschaftenEntwurf.length) ||
    breiteEntwurf !== "bahnbreit" ||
    vkEntwurf[0] !== VK_SPANNE.min ||
    vkEntwurf[1] !== VK_SPANNE.max;

  function zuruecksetzen() {
    entwuerfeLeeren();
    // Der Preis-Schalter ist eine Ansicht, kein Filter – er bleibt wie im ERP stehen.
    navigate({ pathname: "/stoffe", search: stoffListeQuery({ ansicht: params.ansicht, allePreise: params.allePreise }) });
  }

  function herstellerAktiv(an: boolean) {
    herstellerVerzoegert.abbrechen();
    if (!an) return gehe({ herstellerName: "" });
    if (herstellerText.trim()) gehe({ herstellerName: herstellerText.trim() });
    else herstellerFeld.current?.focus();
  }

  function breiteAktiv(an: boolean) {
    if (!an && params.breite) setBreiteEntwurf(params.breite);
    gehe({ breite: an ? breiteEntwurf : null });
  }

  /** Häkchen für Typ, Material und Eigenschaften: ohne Entwurf öffnet es die Auswahl. */
  function listeAktiv<T extends string>(art: Auswahl, an: boolean, url: T[], entwurf: T[], setEntwurf: (w: T[]) => void) {
    const patch = (w: T[]): Partial<StoffListeParams> =>
      art === "typ" ? { typ: w } : art === "material" ? { material: w } : { eigenschaften: w as StoffEigenschaft[] };
    if (!an) {
      setEntwurf(url);
      return gehe(patch([]));
    }
    if (entwurf.length) gehe(patch(entwurf));
    else setAuswahlOffen(art);
  }

  function vkAktivSetzen(an: boolean) {
    if (!an) {
      setVkEntwurf([vkVon, vkBis]);
      return gehe({ vkMin: null, vkMax: null });
    }
    gehe({ vkMin: vkEntwurf[0], vkMax: vkEntwurf[1] });
  }

  function allePreiseSetzen(an: boolean) {
    // Verschwindet die sortierte Spalte, gilt wieder die Standard-Sortierung.
    if (!an && PREIS_SORTIERUNG.includes(params.sort)) {
      return gehe({ allePreise: false, sort: undefined, richtung: undefined });
    }
    gehe({ allePreise: an, seite: params.seite });
  }

  function sortieren(sort: StoffSortierung) {
    gehe({ sort, richtung: params.sort === sort && params.richtung === "asc" ? "desc" : "asc" });
  }

  /* --- Bearbeiten und Aktionen ----------------------------------------------------------- */

  const speichere = useCallback(
    async (id: string, aenderung: Aenderung): Promise<boolean> => {
      try {
        const res = await api.api.admin.stoffe[":id"].$patch({ param: { id }, json: aenderung });
        if (!res.ok) {
          zeigeFehler(await errorMessage(res, "Speichern fehlgeschlagen"));
          return false;
        }
        melde("Gespeichert.");
        neuLaden();
        return true;
      } catch {
        zeigeFehler("Speichern fehlgeschlagen");
        return false;
      }
    },
    [melde, neuLaden, zeigeFehler],
  );

  /** Neuer Typ bzw. neues Material aus der Tabelle; bei 409 gilt der vorhandene Name. */
  const neuerWert = useMemo(() => {
    const fuer = (art: StoffWerteArt) => async (name: string): Promise<string | null> => {
      if (name.includes(",")) {
        zeigeFehler(WERT_OHNE_KOMMA);
        return null;
      }
      try {
        const res = await api.api.admin["stoff-werte"][":art"].$post({ param: { art }, json: { name } });
        if (res.ok) {
          const d = await res.json();
          if ("wert" in d) {
            setWerte((alt) => ({ ...alt, [art]: mergeOptionen(alt[art], [d.wert.name]) }));
            return d.wert.name;
          }
        }
        if (res.status === 409) {
          const vorhanden = (await ladeWerte(art)).find((n) => n.toLowerCase() === name.trim().toLowerCase());
          if (vorhanden) return vorhanden;
        }
        zeigeFehler(await errorMessage(res, "Wert konnte nicht angelegt werden"));
      } catch {
        zeigeFehler("Wert konnte nicht angelegt werden");
      }
      return null;
    };
    return { typ: fuer("typ"), material: fuer("material") };
  }, [ladeWerte, zeigeFehler]);

  const [frage, setFrage] = useState<Frage | null>(null);

  async function aktionAusfuehren() {
    if (!frage) return;
    const { art, stoff } = frage;
    const param = { param: { id: stoff.id } };
    try {
      const res =
        art === "archivieren"
          ? await api.api.admin.stoffe[":id"].$delete(param)
          : art === "wiederherstellen"
            ? await api.api.admin.stoffe[":id"].wiederherstellen.$post(param)
            : await api.api.admin.stoffe[":id"].endgueltig.$delete(param);
      if (res.ok) melde(FRAGEN[art].erfolg);
      else zeigeFehler(await errorMessage(res, `${FRAGEN[art].knopf} fehlgeschlagen`));
    } catch {
      zeigeFehler(`${FRAGEN[art].knopf} fehlgeschlagen`);
    }
    setFrage(null);
    neuLaden();
  }

  /* --- Seiten ---------------------------------------------------------------------------- */

  const gesamt = daten?.gesamt ?? 0;
  const seiten = Math.max(1, Math.ceil(gesamt / STOFFE_PRO_SEITE));

  // Nach dem Archivieren der letzten Zeile einer Seite nicht im Leeren stehen bleiben.
  useEffect(() => {
    if (daten && !laedt && datenAbfrage === abfrage && params.seite > seiten) gehe({ seite: seiten }, true);
  }, [daten, laedt, datenAbfrage, abfrage, params.seite, seiten, gehe]);

  /* --- Ansicht --------------------------------------------------------------------------- */

  const zaehler = daten?.zaehler;
  const filterAktiv = hatFilter(params);
  const listeQuery = stoffListeQuery(params);
  const halt = { onClick: (e: { stopPropagation(): void }) => e.stopPropagation(), onDoubleClick: (e: { stopPropagation(): void }) => e.stopPropagation() };
  const spalten = 14 + (params.allePreise ? 3 : 0) + (imArchiv ? 1 : 0);

  const kopf = (label: string, sort: StoffSortierung, className?: string) => {
    const aktiv = params.sort === sort;
    return (
      <th className={className} aria-sort={aktiv ? (params.richtung === "asc" ? "ascending" : "descending") : undefined}>
        <button type="button" className="sortierkopf stoffe-sortierkopf" onClick={() => sortieren(sort)}>
          {label}
          {aktiv && <Zeichen name={params.richtung === "asc" ? "hoch" : "runter"} className="stoffe-pfeil" />}
        </button>
      </th>
    );
  };

  const reiter = (ansicht: "aktiv" | "archiv", label: string, anzahl: number | undefined) => {
    const aktiv = params.ansicht === ansicht;
    return (
      <Link
        className={aktiv ? "button stoffe-pill stoffe-pill--aktiv" : "button stoffe-pill"}
        aria-current={aktiv ? "page" : undefined}
        to={{ pathname: "/stoffe", search: stoffListeQuery({ ansicht, allePreise: params.allePreise }) }}
      >
        {label}
        {anzahl !== undefined && ` (${anzahl})`}
      </Link>
    );
  };

  return (
    <section className="stoffe">
      <div className="stoffe-kopf">
        <div>
          <h1>Stoffe</h1>
          <p className="muted">
            {imArchiv
              ? "Archivierte Dekostoffe — wiederherstellen oder endgültig löschen."
              : "Dekostoffe — Stammdaten für Konfigurator und Kalkulation."}
          </p>
        </div>
        {!imArchiv && (
          <div className="stoffe-kopf__aktionen">
            {farbabgleich.knoepfe}
            <label className="stoffe-aufschlag">
              <span>Aufschlag zum Fachhändler-EK</span>
              <select
                value={stoffAufschlag ?? ""}
                disabled={stoffAufschlag === null}
                onChange={(e) => void stoffAufschlagSpeichern(Number(e.target.value))}
                aria-label="Aufschlag zum Fachhändler-EK"
              >
                {stoffAufschlag === null && <option value="">…</option>}
                {STOFF_AUFSCHLAG_STUFEN.map((p) => (
                  <option key={p} value={p}>
                    {p} %
                  </option>
                ))}
              </select>
            </label>
            <Link className="button stoffe-pill" to="/stoffe/typen" state={{ liste: listeQuery }}>
              Typen verwalten
            </Link>
            <Link className="button stoffe-pill" to="/stoffe/materialien" state={{ liste: listeQuery }}>
              Materialien verwalten
            </Link>
            <Link className="button stoffe-pill stoffe-pill--primaer" to="/stoffe/neu" state={{ liste: listeQuery }}>
              Neuer Stoff
            </Link>
          </div>
        )}
      </div>

      {farbabgleich.anzeige}

      <nav className="stoffe-reiter" aria-label="Ansicht">
        {reiter("aktiv", "Stoffe", zaehler?.aktiv)}
        {reiter("archiv", "Stoffarchiv", zaehler?.archiv)}
      </nav>

      <div className="stoffe-suchzeile">
        <input
          type="search"
          className="stoffe-suche"
          placeholder="Suchen (Stoffname, Hersteller, Nummer)…"
          aria-label="Suchen (Stoffname, Hersteller, Nummer)"
          value={sucheText}
          onChange={(e) => {
            const text = e.target.value;
            setSucheText(text);
            sucheVerzoegert.planen(() => gehe({ suche: text.trim() }, true));
          }}
        />
        <div className="stoffe-suchzeile__rechts">
          <label className="stoffe-schalter">
            <input type="checkbox" checked={params.allePreise} onChange={(e) => allePreiseSetzen(e.target.checked)} />
            Alle Preise (EK, Faktor, Gewinn)
          </label>
          <button type="button" className="stoffe-zuruecksetzen" disabled={!zuruecksetzbar} onClick={zuruecksetzen}>
            <Zeichen name="kreuz" />
            Filter zurücksetzen
          </button>
        </div>
      </div>

      <div className="stoffe-filter" role="group" aria-label="Filter">
        <FilterFeld label="Hersteller" aktiv={Boolean(params.herstellerName)} onAktiv={herstellerAktiv}>
          <input
            ref={herstellerFeld}
            type="text"
            className="stoffe-feld stoffe-feld--gefuellt"
            placeholder="z. B. Ado"
            aria-label="Hersteller"
            value={herstellerText}
            onChange={(e) => {
              const text = e.target.value;
              setHerstellerText(text);
              herstellerVerzoegert.planen(() => gehe({ herstellerName: text.trim() }, true));
            }}
          />
        </FilterFeld>

        <FilterFeld label="Breite" aktiv={params.breite !== null} onAktiv={breiteAktiv} breit>
          <select
            className="stoffe-feld"
            aria-label="Breite"
            value={params.breite ?? breiteEntwurf}
            onChange={(e) => {
              const breite = e.target.value as BreitenKlasse;
              setBreiteEntwurf(breite);
              gehe({ breite });
            }}
          >
            <option value="bahnbreit">Bahnbreit (135–160 cm)</option>
            <option value="raumhoch">Raumhoch (280–350 cm)</option>
          </select>
        </FilterFeld>

        <FilterFeld
          label="Typ"
          aktiv={params.typ.length > 0}
          onAktiv={(an) => listeAktiv("typ", an, params.typ, typEntwurf, setTypEntwurf)}
        >
          <MehrfachAuswahl
            platzhalter="Typ wählen…"
            label="Typ"
            optionen={werte.typ}
            gewaehlt={typWerte}
            offen={auswahlOffen === "typ"}
            onOffen={(o) => setAuswahlOffen(o ? "typ" : null)}
            onChange={(typ) => {
              setTypEntwurf(typ);
              gehe({ typ });
            }}
          />
        </FilterFeld>

        <FilterFeld
          label="Material"
          aktiv={params.material.length > 0}
          onAktiv={(an) => listeAktiv("material", an, params.material, materialEntwurf, setMaterialEntwurf)}
        >
          <MehrfachAuswahl
            platzhalter="Material wählen…"
            label="Material"
            optionen={werte.material}
            gewaehlt={materialWerte}
            offen={auswahlOffen === "material"}
            onOffen={(o) => setAuswahlOffen(o ? "material" : null)}
            onChange={(material) => {
              setMaterialEntwurf(material);
              gehe({ material });
            }}
          />
        </FilterFeld>

        <FilterFeld
          label="Eigenschaften"
          aktiv={params.eigenschaften.length > 0}
          onAktiv={(an) =>
            listeAktiv("eigenschaften", an, params.eigenschaften, eigenschaftenEntwurf, setEigenschaftenEntwurf)
          }
        >
          <MehrfachAuswahl
            platzhalter="Eigenschaften…"
            label="Eigenschaften"
            optionen={EIGENSCHAFTEN.map((e) => e.label)}
            gewaehlt={EIGENSCHAFTEN.filter((e) => eigenschaftenWerte.includes(e.wert)).map((e) => e.label)}
            offen={auswahlOffen === "eigenschaften"}
            onOffen={(o) => setAuswahlOffen(o ? "eigenschaften" : null)}
            onChange={(labels) => {
              const eigenschaften: StoffEigenschaft[] = EIGENSCHAFTEN.filter((e) => labels.includes(e.label)).map(
                (e) => e.wert,
              );
              setEigenschaftenEntwurf(eigenschaften);
              gehe({ eigenschaften });
            }}
          />
        </FilterFeld>

        <FilterFeld label={`VK-Preis: ${vkVon}–${vkBis} €`} aktiv={vkAktiv} onAktiv={vkAktivSetzen} breit>
          <PreisSpanne
            von={vkVon}
            bis={vkBis}
            onChange={(von, bis) => setVkZiehen([von, bis])}
            onFertig={(von, bis) => {
              setVkZiehen(null);
              setVkEntwurf([von, bis]);
              // Bei schon aktivem Filter ersetzt die Spanne den Eintrag – sonst wäre jeder Tastendruck ein Zurück-Schritt.
              gehe({ vkMin: von, vkMax: bis }, vkAktiv);
            }}
          />
        </FilterFeld>
      </div>

      <div className="stoffe-statuszeile" role="status">
        {meldung}
      </div>
      {(fehler || ladeFehler) && (
        <p className="error" role="alert">
          {fehler ?? ladeFehler}
        </p>
      )}

      {daten === null ? (
        !ladeFehler && <p className="muted">Lade Stoffe …</p>
      ) : (
        <>
          <div className={laedt ? "tabelle-rahmen stoffe-rahmen stoffe-rahmen--laedt" : "tabelle-rahmen stoffe-rahmen"} aria-busy={laedt}>
            <table className="table stoffe-tabelle">
              <thead>
                <tr>
                  {kopf("#", "nr", "stoffe-nr")}
                  {kopf("Hersteller", "hersteller")}
                  {kopf("Stoffname", "name")}
                  {kopf("Nummer", "nummer")}
                  {kopf("Breite", "breite")}
                  {kopf("Rapport", "rapport")}
                  <th>Typ</th>
                  <th>Material</th>
                  <th>Eigenschaften</th>
                  {params.allePreise && kopf("EK", "ek", "num")}
                  {params.allePreise && kopf("Faktor", "faktor", "num")}
                  {kopf("VK brutto", "vk", "num")}
                  {params.allePreise && kopf("Gewinn", "gewinn", "num")}
                  <th>Foto</th>
                  <th>Notiz</th>
                  {imArchiv && <th>Ort</th>}
                  {imArchiv ? kopf("Archiviert am", "archiviert") : kopf("Geändert", "aktualisiert")}
                  <th className="stoffe-aktionen-kopf">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {daten.stoffe.length === 0 ? (
                  <tr className="stoffe-leer">
                    <td colSpan={spalten}>
                      {filterAktiv
                        ? "Keine Treffer — Filter anpassen oder zurücksetzen."
                        : imArchiv
                          ? "Das Stoffarchiv ist leer."
                          : "Noch keine Stoffe."}
                    </td>
                  </tr>
                ) : (
                  daten.stoffe.map((s) => {
                    const ek = s.ekProLfmCents;
                    const eigenschaften = { schwerEntflammbar: s.schwerEntflammbar, recyclebar: s.recyclebar };
                    return (
                      <tr
                        key={s.id}
                        className={gewaehlt === s.id ? "stoffe-zeile stoffe-zeile--gewaehlt" : "stoffe-zeile"}
                        onClick={() => setGewaehlt(s.id)}
                        onDoubleClick={() => !imArchiv && navigate(`/stoffe/${s.id}`, { state: { liste: listeQuery } })}
                      >
                        <td className="stoffe-nr">{s.ordnungsnummer ?? <Leer />}</td>
                        <td className="stoffe-hersteller">{s.herstellerName}</td>
                        <td>
                          {imArchiv ? (
                            s.name
                          ) : (
                            <Link
                              className="stoffe-name"
                              to={`/stoffe/${s.id}`}
                              state={{ liste: listeQuery }}
                              // Mausklick markiert nur die Zeile (Doppelklick öffnet); Tastatur und Strg-/Cmd-Klick öffnen.
                              onClick={(e) => {
                                if (e.detail > 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey) e.preventDefault();
                              }}
                            >
                              {s.name}
                            </Link>
                          )}
                          {!s.aktiv && <span className="badge badge--suspended stoffe-inaktiv">inaktiv</span>}
                        </td>
                        <td>{s.nummer || <Leer />}</td>
                        <td className="nummer">{formatCm(s.ballenbreiteMm)}</td>
                        <td className="nummer">{formatRapport(s.rapportMm)}</td>
                        {imArchiv ? (
                          <>
                            <td>
                              <Badges werte={splitListe(s.typ)} />
                            </td>
                            <td>
                              <Badges werte={splitListe(s.material)} />
                            </td>
                            <td>
                              <EigenschaftenSymbole werte={eigenschaften} />
                            </td>
                          </>
                        ) : (
                          <>
                            <td {...halt}>
                              <WerteZelle
                                wert={s.typ}
                                bezeichnung="Typ"
                                optionen={werte.typ}
                                neuerWert={neuerWert.typ}
                                onSpeichern={(typ) => speichere(s.id, { typ })}
                              />
                            </td>
                            <td {...halt}>
                              <WerteZelle
                                wert={s.material}
                                bezeichnung="Material"
                                optionen={werte.material}
                                neuerWert={neuerWert.material}
                                onSpeichern={(material) => speichere(s.id, { material })}
                              />
                            </td>
                            <td {...halt}>
                              <EigenschaftenZelle werte={eigenschaften} onSpeichern={(neu) => speichere(s.id, neu)} />
                            </td>
                          </>
                        )}
                        {params.allePreise && <td className="num">{ek > 0 ? euroText(ek) : <Leer />}</td>}
                        {params.allePreise && <td className="num">{faktorText(s.faktor)}</td>}
                        <td className="num">{ek > 0 ? euroText(vkBruttoCents(ek, s.faktor)) : <Leer />}</td>
                        {params.allePreise && (
                          <td className="num">{ek > 0 ? euroText(gewinnCents(ek, s.faktor)) : <Leer />}</td>
                        )}
                        <td {...halt}>
                          <FotoZelle url={s.bildUrl} name={s.name} />
                        </td>
                        <td {...halt}>
                          <NotizZelle notiz={s.notiz} />
                        </td>
                        {imArchiv && <td>{s.lagerort || <Leer />}</td>}
                        <td className="nummer">
                          {datumText(imArchiv ? s.archiviertAm : s.aktualisiertAm) ?? <Leer />}
                        </td>
                        <td {...halt}>
                          {imArchiv ? (
                            <span className="stoffe-aktionen">
                              <button
                                type="button"
                                className="stoffe-aktion"
                                onClick={() => setFrage({ art: "wiederherstellen", stoff: s })}
                              >
                                Wiederherstellen
                              </button>
                              <button
                                type="button"
                                className="stoffe-aktion danger"
                                onClick={() => setFrage({ art: "loeschen", stoff: s })}
                              >
                                Endgültig löschen
                              </button>
                            </span>
                          ) : (
                            <span className="stoffe-aktionen">
                              <button
                                type="button"
                                className="stoffe-symbolknopf"
                                title="Archivieren"
                                aria-label={`„${s.name}“ archivieren`}
                                onClick={() => setFrage({ art: "archivieren", stoff: s })}
                              >
                                <Zeichen name="archiv" />
                              </button>
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="stoffe-fuss">
            <span className="muted small">
              {gesamt} {gesamt === 1 ? "Stoff" : "Stoffe"}
              {filterAktiv && zaehler && ` (gefiltert von ${imArchiv ? zaehler.archiv : zaehler.aktiv})`}
            </span>
            <div className="actions">
              <button type="button" disabled={params.seite <= 1} onClick={() => gehe({ seite: params.seite - 1 })}>
                Zurück
              </button>
              <span className="muted small">
                Seite {Math.min(params.seite, seiten)} von {seiten}
              </span>
              <button type="button" disabled={params.seite >= seiten} onClick={() => gehe({ seite: params.seite + 1 })}>
                Weiter
              </button>
            </div>
          </div>
        </>
      )}

      <Bestaetigen
        offen={frage !== null}
        titel={frage ? FRAGEN[frage.art].titel : ""}
        text={frage ? FRAGEN[frage.art].text(frage.stoff.name) : undefined}
        bestaetigen={frage ? FRAGEN[frage.art].knopf : ""}
        gefaehrlich={frage?.art === "loeschen"}
        onBestaetigen={aktionAusfuehren}
        onAbbrechen={() => setFrage(null)}
      />
    </section>
  );
}

